import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import {
  selectActiveBranchId,
  selectIsCompanyMode,
  selectPageFilter
} from "../../Branches/redux/branches.slice.ts"
import type { RootState } from "../../../store.ts"
import { type BaseApiResponse } from "../../../types/api.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import * as services from "../api/services"
import type {
  CreateReportData,
  GetReportsParams
} from "../api/services.types.ts"
import type { ReportSettingsPatch } from "../helpers/reportSettings.ts"
import type { ReportDefinitionV1 } from "../types/canvas.types.ts"
import { folderRequestErrorMessage } from "../helpers/reportFolders.ts"
import type {
  ReportDetail,
  ReportFolderRelabelResult,
  ReportFolderRow,
  ReportListItem
} from "../types/types.ts"
import { reportsSliceName } from "./reports.constants.ts"

const unwrapDetail = (data: unknown): ReportDetail | null =>
  (data as BaseApiResponse<ReportDetail> | undefined)?.details?.data ?? null

const buildReportsParams = (filters: GetReportsParams): GetReportsParams => ({
  ...(filters.search ? { search: filters.search } : {}),
  ...(filters.favorites ? { favorites: true } : {}),
  ...(filters.shared ? { shared: true } : {})
})

// Company HQ's own branch filter for the Reports tab — mirrors
// `calendarBranchScopeParams` exactly: a specific global-workspace branch
// always wins; in Company HQ mode, the page's own saved subset narrows
// further, and "All branches" sends no param at all (the unscoped
// queryset already shows everything the caller can see).
export const reportsBranchScopeParams = (
  state: RootState
): { team?: string; teams?: string } => {
  const isCompanyMode = selectIsCompanyMode(state)
  const activeBranchId = selectActiveBranchId(state)
  if (!isCompanyMode) return activeBranchId ? { team: activeBranchId } : {}

  const pageBranchScope = selectPageFilter("reports")(state)
  return pageBranchScope.type === "subset"
    ? { teams: pageBranchScope.teamIds.join(",") }
    : {}
}

export const fetchReports = createAsyncThunk<
  ReportListItem[],
  GetReportsParams
>(
  `${reportsSliceName}/fetchReports`,
  async (filters, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState
    const response = await services.getReports({
      ...buildReportsParams(filters),
      ...reportsBranchScopeParams(state)
    })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as ReportListItem[]
  }
)

export const createReport = createAsyncThunk<
  BaseApiResponse<ReportDetail>,
  CreateReportData
>(
  `${reportsSliceName}/createReport`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createReport(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    // A draft is the wizard's working copy; the report is created, and said to
    // be, only when the wizard finishes it.
    if (!data.is_draft)
      showResponseMessage(response, "Report created successfully.")
    return response.data as BaseApiResponse<ReportDetail>
  }
)

export const fetchReportDetails = createAsyncThunk<ReportDetail, string>(
  `${reportsSliceName}/fetchReportDetails`,
  async (reportId, { dispatch, rejectWithValue }) => {
    const response = await services.getReportDetails(reportId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as ReportDetail
  }
)

export interface ToggleReportFavoriteArg {
  id: string
  previousIsFavorite: boolean
}

export const toggleReportFavorite = createAsyncThunk<
  { id: string; is_favorite: boolean },
  ToggleReportFavoriteArg
>(
  `${reportsSliceName}/toggleReportFavorite`,
  async ({ id }, { dispatch, rejectWithValue }) => {
    const response = await services.toggleReportFavorite(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    const data = response.data as BaseApiResponse<{ is_favorite: boolean }>
    return { id, is_favorite: !!data?.details?.data?.is_favorite }
  }
)

export interface DuplicateReportArg {
  id: string
  name: string
  description?: string | null
  tags?: string[]
  visibility?: ReportListItem["visibility"]
  /** The copy is filed beside its original. */
  folder?: string
  definition?: ReportDefinitionV1
}

export const duplicateReport = createAsyncThunk<
  ReportDetail | null,
  DuplicateReportArg
>(
  `${reportsSliceName}/duplicateReport`,
  async (arg, { dispatch, rejectWithValue }) => {
    let definition = arg.definition
    if (!definition) {
      const source = await services.getReportDetails(arg.id)
      if (!source.ok) {
        dispatch(errorSave(source?.data?.details))
        return rejectWithValue(source.data)
      }
      definition = (source.data as ReportDetail)
        ?.definition as ReportDefinitionV1
    }

    const payload: CreateReportData = {
      name: `${arg.name} (copy)`,
      description: arg.description ?? undefined,
      tags: arg.tags,
      visibility: arg.visibility,
      ...(arg.folder ? { folder: arg.folder } : {}),
      definition
    }
    const response = await services.createReport(payload)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }
    showResponseMessage(response, "Report duplicated.")
    return unwrapDetail(response.data)
  }
)

export interface RenameReportArg {
  id: string
  name: string
  previousName: string
}

export const renameReport = createAsyncThunk<
  { id: string; name: string },
  RenameReportArg
>(
  `${reportsSliceName}/renameReport`,
  async ({ id, name }, { dispatch, rejectWithValue }) => {
    const response = await services.updateReport(id, { name })
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }
    return { id, name }
  }
)

export interface UpdateReportSettingsArg {
  id: string
  patch: ReportSettingsPatch
}

export interface UpdateReportSettingsResult {
  id: string
  patch: ReportSettingsPatch
  report: ReportDetail | null
}

// Unlike renameReport this is not optimistic: the settings modal blocks on the
// request, so there is never an unconfirmed value on screen to roll back.
export const updateReportSettings = createAsyncThunk<
  UpdateReportSettingsResult,
  UpdateReportSettingsArg
>(
  `${reportsSliceName}/updateReportSettings`,
  async ({ id, patch }, { dispatch, rejectWithValue }) => {
    const response = await services.updateReport(id, patch)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }
    return { id, patch, report: unwrapDetail(response.data) }
  }
)

export interface DeleteReportArg {
  id: string
}

export const deleteReport = createAsyncThunk<{ id: string }, DeleteReportArg>(
  `${reportsSliceName}/deleteReport`,
  async ({ id }, { dispatch, rejectWithValue }) => {
    const response = await services.deleteReport(id)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }
    return { id }
  }
)

export const fetchReportFolders = createAsyncThunk<string[], void>(
  `${reportsSliceName}/fetchReportFolders`,
  async (_arg, { rejectWithValue }) => {
    const response = await services.getReportFolders()
    if (!response.ok) return rejectWithValue(response.data)
    return ((response.data as ReportFolderRow[] | undefined) ?? []).map(
      (row) => row.name
    )
  }
)

/** A refused folder request keeps the dialog open with this message in it. */
export interface ReportFolderRejection {
  message: string | null
}

export const createReportFolder = createAsyncThunk<
  string,
  { name: string },
  { rejectValue: ReportFolderRejection }
>(
  `${reportsSliceName}/createReportFolder`,
  async ({ name }, { rejectWithValue }) => {
    const response = await services.createReportFolder(name)
    if (!response.ok)
      return rejectWithValue({
        message: folderRequestErrorMessage(response.data)
      })
    return response.data?.details?.data?.name ?? name.trim()
  }
)

// The server relabels every report in the folder the caller may edit, so the
// list is fetched again rather than guessed at here.
const refetchReports = (
  dispatch: (action: unknown) => unknown,
  state: RootState
) => dispatch(fetchReports(state.reports.filters))

export const renameReportFolder = createAsyncThunk<
  ReportFolderRelabelResult & { previousName: string },
  { name: string; newName: string },
  { rejectValue: ReportFolderRejection }
>(
  `${reportsSliceName}/renameReportFolder`,
  async ({ name, newName }, { dispatch, getState, rejectWithValue }) => {
    const response = await services.renameReportFolder(name, newName)
    if (!response.ok)
      return rejectWithValue({
        message: folderRequestErrorMessage(response.data)
      })
    const result = response.data?.details?.data
    void refetchReports(dispatch, getState() as RootState)
    return {
      name: result?.name ?? newName.trim(),
      updated: result?.updated ?? 0,
      skipped: result?.skipped ?? 0,
      previousName: name
    }
  }
)

export const deleteReportFolder = createAsyncThunk<
  ReportFolderRelabelResult,
  { name: string },
  { rejectValue: ReportFolderRejection }
>(
  `${reportsSliceName}/deleteReportFolder`,
  async ({ name }, { dispatch, getState, rejectWithValue }) => {
    const response = await services.deleteReportFolder(name)
    if (!response.ok)
      return rejectWithValue({
        message: folderRequestErrorMessage(response.data)
      })
    const result = response.data?.details?.data
    void refetchReports(dispatch, getState() as RootState)
    return {
      name,
      updated: result?.updated ?? 0,
      skipped: result?.skipped ?? 0
    }
  }
)

export interface MoveReportsResult {
  folder: string
  moved: ReportDetail[]
  failed: { id: string; message: string | null }[]
}

// Moving is the same PATCH of `folder` the settings modal sends, one report at
// a time, so a refusal on one report never undoes the others. Only the
// `folder` column is written: owner, visibility and shares stay as they were.
const MOVE_CONCURRENCY = 4

export const moveReportsToFolder = createAsyncThunk<
  MoveReportsResult,
  { ids: string[]; folder: string }
>(`${reportsSliceName}/moveReportsToFolder`, async ({ ids, folder }) => {
  const moved: ReportDetail[] = []
  const failed: MoveReportsResult["failed"] = []
  const queue = [...ids]
  const worker = async () => {
    for (let id = queue.shift(); id; id = queue.shift()) {
      const response = await services.updateReport(id, { folder })
      const report = unwrapDetail(response.data)
      if (response.ok && report) moved.push(report)
      else
        failed.push({ id, message: folderRequestErrorMessage(response.data) })
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(MOVE_CONCURRENCY, ids.length) }, worker)
  )
  return { folder, moved, failed }
})
